import { computed } from 'vue'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'

// Centrální definice všech podporovaných jazyků
const availableLanguages = [
  { code: 'cs', name: 'Čeština', flag: 'cz' },
  { code: 'sk', name: 'Slovenština', flag: 'sk' },
  { code: 'pl', name: 'Polština', flag: 'pl' },
  { code: 'en', name: 'Angličtina', flag: 'gb' },
  { code: 'fr', name: 'Francouzština', flag: 'fr' },
  { code: 'hr', name: 'Chorvatština', flag: 'hr' },
  { code: 'hu', name: 'Maďarština', flag: 'hu' },
  { code: 'bg', name: 'Bulharština', flag: 'bg' },
  { code: 'de', name: 'Němčina', flag: 'de' },
  { code: 'es', name: 'Španělština', flag: 'es' },
  { code: 'it', name: 'Italština', flag: 'it' },
  { code: 'nl', name: 'Nizozemština', flag: 'nl' },
  { code: 'ro', name: 'Rumunština', flag: 'ro' },
  { code: 'sl', name: 'Slovinština', flag: 'si' },
]

// Centrální definice podporovaných měn
const availableCurrencies = [
  { code: 'CZK', name: 'Česká koruna', symbol: 'Kč', format: '%s Kč' },
  { code: 'EUR', name: 'Euro', symbol: '€', format: '%s €' },
  { code: 'PLN', name: 'Polský zlotý', symbol: 'zł', format: '%s zł' }
]

export function useLocalizedData() {
  const projectsStore = useProjectsStore()
  
  // Get currently available locales from the project
  const projectLocales = computed(() => {
    return projectsStore.currentProject?.locales || ['cs']
  })
  
  // Get the default locale from the project
  const defaultLocale = computed(() => {
    return projectsStore.currentProject?.defaultLocale || 'cs'
  })
  
  // Generate localeToFlagMap dynamically from availableLanguages
  const localeToFlagMap = computed(() => {
    const map: Record<string, string> = {}
    availableLanguages.forEach(lang => {
      map[lang.code] = lang.flag
    })
    return map
  })
  
  // Generate localeToLanguageMap dynamically from availableLanguages
  const localeToLanguageMap = computed(() => {
    const map: Record<string, string> = {}
    availableLanguages.forEach(lang => {
      map[lang.code] = lang.name
    })
    return map
  })
  
  // Get flag code for a locale
  const getLocaleFlag = (locale: string): string => {
    return localeToFlagMap.value[locale] || locale
  }
  
  // Get language name for a locale
  const getLocaleLabel = (locale: string): string => {
    return localeToLanguageMap.value[locale] || locale
  }
  
  // Get available languages for the current project
  const projectLanguages = computed(() => {
    return availableLanguages.filter(lang => projectLocales.value.includes(lang.code))
  })
  
  // Currency related operations
  const getCurrencySymbol = (currencyCode: string): string => {
    const currency = availableCurrencies.find(c => c.code === currencyCode)
    return currency?.symbol || currencyCode
  }
  
  const getCurrencyName = (currencyCode: string): string => {
    const currency = availableCurrencies.find(c => c.code === currencyCode)
    return currency?.name || currencyCode
  }
  
  const formatCurrency = (value: string | number, currencyCode: string): string => {
    const currency = availableCurrencies.find(c => c.code === currencyCode)
    if (!currency) return `${value} ${currencyCode}`
    
    return currency.format.replace('%s', String(value))
  }
  
  const getCurrencyOptions = () => {
    return availableCurrencies.map(currency => ({
      value: currency.code,
      label: `${currency.symbol} (${currency.code})`
    }))
  }

  return {
    // Language related
    localeToFlagMap,
    localeToLanguageMap,
    availableLanguages,
    projectLocales,
    defaultLocale,
    getLocaleFlag,
    getLocaleLabel,
    projectLanguages,
    
    // Currency related
    availableCurrencies,
    getCurrencySymbol,
    getCurrencyName,
    formatCurrency,
    getCurrencyOptions
  }
}